
import { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableSectionItem } from './SortableSectionItem';
import { initialSections } from '@/utils/pdf-templates/constants';
import { Column } from '@/utils/pdf-templates/types';
import { Button } from '@/components/ui/button';
import { PlusCircle, Trash2, GripVertical } from 'lucide-react';
import { ColumnContainer } from './ColumnContainer';

interface SectionSettingsProps {
    columns: Column[];
    onColumnsChange: (newColumns: Column[]) => void;
    gridCols: number;
}

const AVAILABLE_SECTIONS_ID = 'available_sections';

export function SectionSettings({ columns, onColumnsChange, gridCols }: SectionSettingsProps) {
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const usedSectionIds = useMemo(() => new Set(columns.flatMap(col => col.sections.map(s => s.id))), [columns]);
    const availableSections = useMemo(() => initialSections.filter(s => !usedSectionIds.has(s.id)), [usedSectionIds]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
    
        if (!over) return;
    
        const activeId = String(active.id);
        const overId = String(over.id);
        const activeContainer = active.data.current?.sortable.containerId;
        const overContainer = over.data.current?.sortable?.containerId || over.id;
    
        if (!activeContainer || !overContainer) return;

        // Reordering in same container
        if (activeContainer === overContainer) {
            if (activeId !== overId && activeContainer !== AVAILABLE_SECTIONS_ID) {
                const newColumns = columns.map(col => {
                    if (col.id === activeContainer) {
                        const oldIndex = col.sections.findIndex(s => s.id === activeId);
                        const newIndex = col.sections.findIndex(s => s.id === overId);
                        if (oldIndex > -1 && newIndex > -1) {
                            return { ...col, sections: arrayMove(col.sections, oldIndex, newIndex) };
                        }
                    }
                    return col;
                });
                onColumnsChange(newColumns);
            }
            return;
        }
    
        // Moving between containers
        const activeItem = initialSections.find(s => s.id === activeId);
        if (!activeItem) return;
    
        let newColumns = [...columns];
        
        // 1. Remove from source container
        if (activeContainer !== AVAILABLE_SECTIONS_ID) {
            newColumns = newColumns.map(col => {
                if (col.id === activeContainer) {
                    return { ...col, sections: col.sections.filter(s => s.id !== activeId) };
                }
                return col;
            });
        }
    
        // 2. Add to destination container
        if (overContainer !== AVAILABLE_SECTIONS_ID) {
            newColumns = newColumns.map(col => {
                if (col.id === overContainer) {
                    const overIndex = col.sections.findIndex(s => s.id === overId);
                    const newSections = [...col.sections];
    
                    if (overIndex !== -1) {
                        newSections.splice(overIndex, 0, activeItem);
                    } else {
                        newSections.push(activeItem);
                    }
                    return { ...col, sections: newSections };
                }
                return col;
            });
        }
    
        onColumnsChange(newColumns);
    };
    
    const addColumn = () => {
        const newColumnId = `column-${Date.now()}`;
        onColumnsChange([...columns, { id: newColumnId, sections: [] }]);
    };
    
    const removeColumn = (columnId: string) => {
        onColumnsChange(columns.filter(col => col.id !== columnId));
    };

    const activeSection = activeId ? initialSections.find(s => s.id === activeId) : null;
    
    const gridClasses: { [key: number]: string } = {
        1: 'grid-cols-1',
        2: 'grid-cols-1 sm:grid-cols-2',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 bg-gray-50 p-3 rounded-lg border">
                    <h3 className="font-semibold mb-3 text-center text-sm text-gray-600">الأقسام المتاحة</h3>
                    <SortableContext id={AVAILABLE_SECTIONS_ID} items={availableSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 min-h-[100px]">
                            {availableSections.map(section => (
                                <SortableSectionItem key={section.id} id={section.id}>
                                    {section.name}
                                </SortableSectionItem>
                            ))}
                             {availableSections.length === 0 && <p className="text-xs text-gray-400 text-center py-4">جميع الأقسام مستخدمة</p>}
                        </div>
                    </SortableContext>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <div className={`grid gap-4 ${gridClasses[gridCols] || 'grid-cols-1 sm:grid-cols-2'}`}>
                        {columns.map(column => (
                             <div key={column.id} className="bg-white p-3 rounded-lg border flex flex-col">
                                <div className="flex justify-between items-center mb-3 flex-shrink-0">
                                    <h3 className="font-semibold text-sm text-gray-700">العمود</h3>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeColumn(column.id)}>
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                </div>
                                <ColumnContainer column={column} />
                            </div>
                        ))}
                    </div>
                     <Button variant="outline" onClick={addColumn} className="w-full h-24 border-dashed">
                        <PlusCircle className="w-5 h-5 mr-2" />
                        إضافة عمود
                    </Button>
                </div>
            </div>
             <DragOverlay>
                {activeSection ? (
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border text-sm shadow-lg">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                        <div className="flex-grow">{activeSection.name}</div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
